import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import { CheckCircle, Error } from '@mui/icons-material';
import DialogGlynt from '../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogActionsGlynt from '../common/Dialog/DialogActionsGlynt';
import DialogContentGlynt from '../common/Dialog/DialogContentGlynt';
import { useAtom } from 'jotai';
import { bulkCorrectionSummaryAtom } from '../../atoms/spreadsheet-atoms';
import { Link, useParams } from 'react-router';
import { LinkTableStyled } from '../common/StyledComponents';

const BulkCorrectionSummaryTableDialog: React.FC = () => {
  const { dpid, id, eid } = useParams<{
    dpid: string;
    id: string;
    eid: string;
  }>();

  if (!dpid || !id) return null;

  const [correctionSummary, setCorrectionSummary] = useAtom(bulkCorrectionSummaryAtom);

  const total = correctionSummary.length;
  const successCount = correctionSummary.filter((status) => status.status === 'success').length;
  const errorCount = total - successCount;

  const handleClose = () => {
    setCorrectionSummary([]);
  };

  // Only render if there's data to show
  if (correctionSummary.length === 0) {
    return null;
  }

  return (
    <DialogGlynt
      open={true}
      closeModal={handleClose}
      width="850px"
      cypressId="dialog-bulk-correction-summary"
    >
      <DialogTitleGlynt title="Bulk Correction Summary" closeModal={handleClose} />
      <DialogContentGlynt>
        <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mb: 2 }}>
          <Chip
            icon={<CheckCircle />}
            label={`${successCount} Successful`}
            color="success"
            variant="outlined"
          />
          {errorCount > 0 && (
            <Chip
              icon={<Error />}
              label={`${errorCount} Failed`}
              color="error"
              variant="outlined"
            />
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            height: '60vh',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'calc(100vh - 300px)',
          }}
        >
          <TableContainer
            component={Paper}
            sx={{
              flex: 1,
              overflow: 'auto',
              '& .MuiTable-root': {
                tableLayout: 'fixed',
              },
              '& .MuiTableCell-root': {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
              '& .MuiTableBody-root': {
                '& .MuiTableRow-root': {
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                },
              },
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell width="5%" align="center">
                    #
                  </TableCell>
                  <TableCell width="20%" align="center">
                    Document ID
                  </TableCell>
                  <TableCell width="20%" align="center">
                    Extraction ID
                  </TableCell>
                  <TableCell width="20%" align="center">
                    Status
                  </TableCell>
                  <TableCell width="35%" align="center">
                    Details
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {correctionSummary.map((status, index) => (
                  <TableRow
                    key={`${status.documentId}-${index}`}
                    sx={{
                      backgroundColor: status.status === 'error' ? 'error.lighter' : 'inherit',
                    }}
                  >
                    <TableCell align="center">{status.documentIndex + 1}</TableCell>
                    <TableCell title={status.documentId} align="center">
                      <LinkTableStyled
                        to={`/data-pools/${dpid}/documents/${status.documentId}`}
                        target="_blank"
                      >
                        {status.documentId}
                      </LinkTableStyled>
                    </TableCell>
                    <TableCell title={status.extractionId} align="center">
                      <LinkTableStyled
                        to={`/data-pools/${dpid}/extraction-batch/${id}/review/${status.extractionId}`}
                        target="_blank"
                      >
                        {status.extractionId}
                      </LinkTableStyled>
                    </TableCell>

                    <TableCell align="center">
                      <Chip
                        icon={status.status === 'success' ? <CheckCircle /> : <Error />}
                        label={status.status}
                        color={status.status === 'success' ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {status.error && (
                        <Typography variant="body2" color="error" title={status.error}>
                          {status.error}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </DialogContentGlynt>

      <DialogActionsGlynt
        handleSubmit={handleClose}
        closeModal={handleClose}
        showClearButton={false}
        cancelCypressId="btn-bulk-correction-summary-cancel"
        submitCypressId="btn-bulk-correction-summary-close"
        clearCypressId="btn-bulk-correction-summary-clear"
        submitLoading={false}
        showCancelButton={false}
        submitButtonText="Close"
      />
    </DialogGlynt>
  );
};

export default BulkCorrectionSummaryTableDialog;
